import SecurityIcon from '@mui/icons-material/Security';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import React from 'react';

import { useAuth } from '@/auth';
import { muiPalette } from '@/shared/general-theme';

interface HeroSectionProps {
  scrollToMaturitySection: () => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({
  scrollToMaturitySection,
}) => {
  const { login } = useAuth();

  return (
    <Box
      sx={{
        pt: { xs: 15, md: 25 },
        pb: { xs: 10, md: 15 },
        position: 'relative',
        zIndex: 1,
        textAlign: 'center',
      }}
    >
      <Container maxWidth='md'>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 0.5,
              borderRadius: '100px',
              backgroundColor: `${muiPalette.secondary.main}20`,
              border: `1px solid ${muiPalette.secondary.main}40`,
              color: muiPalette.secondary.main,
              mb: 4,
              fontSize: '0.875rem',
              fontWeight: 600,
            }}
          >
            <SecurityIcon sx={{ fontSize: '1rem' }} />
            Enterprise-Grade Product Security
          </Box>

          <Typography
            variant='h1'
            sx={{
              fontSize: { xs: '2.5rem', md: '4.5rem' },
              lineHeight: 1.1,
              mb: 3,
              background: `linear-gradient(to bottom, #FFFFFF 0%, ${muiPalette.grey[400]} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Secure Software,<br /> Secure Business
          </Typography>

          <Typography
            variant='h5'
            sx={{
              color: muiPalette.grey[200],
              mb: 5,
              fontWeight: 400,
              maxWidth: '700px',
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            Welcome to <span style={{ color: muiPalette.primary.main, fontWeight: 700 }}>Platypus</span>.
            Empowering organizations to mature product security at scale while nurturing high-performing individuals.
          </Typography>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent='center'
          >
            <Button
              variant='contained'
              size='large'
              sx={{
                fontSize: '1.1rem',
              }}
              onClick={login}
            >
              Get Started
            </Button>
            <Button
              variant='outlined'
              size='large'
              startIcon={<TrendingUpIcon />}
              sx={{
                fontSize: '1.1rem',
                color: muiPalette.grey[50],
                backgroundColor: 'transparent',
                borderColor: muiPalette.grey[300],

                '&:hover': {
                  backgroundColor: muiPalette.grey[900],
                },
              }}
              onClick={scrollToMaturitySection}
            >
              Explore Maturity
            </Button>
          </Stack>
        </motion.div>
      </Container>
    </Box>
  );
};

export default HeroSection;
